import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RELICS } from './relics';
import './RelicStoreOffers.css';

const getOfferExit = (id, index, reduceMotion) => (selectedId) => {
  if (reduceMotion) return { opacity: 0, transition: { duration: 0.15 } };
  if (selectedId === id) {
    return {
      opacity: [1, 1, 0, 0],
      y: [0, -8, -24, -24],
      scale: [1, 1.03, 0.94, 0.94],
      transition: { duration: 0.85, ease: 'easeOut' },
    };
  }
  return {
    opacity: [1, 0.8, 0.85, 0.5, 0.55, 0.15, 0],
    x: [0, -6, 4, -3, 5, 0],
    skewX: [0, -3, 4, -2, 3, 0],
    filter: [
      'blur(0px)',
      'blur(0px)',
      'blur(1px)',
      'blur(0px)',
      'blur(3px)',
      'blur(5px)',
    ],
    clipPath: [
      'inset(0% 0% 0% 0%)',
      'inset(8% 0% 5% 0%)',
      'inset(0% 0% 0% 0%)',
      'inset(25% 0% 18% 0%)',
      'inset(10% 0% 36% 0%)',
      'inset(48% 0% 48% 0%)',
    ],
    '--store-glitch-opacity': [0, 0.35, 0.12, 0.4, 0.18, 0],
    transition: { duration: 0.85, delay: index * 0.06, ease: 'linear' },
  };
};

const RelicStoreOffers = ({
  offers,
  purchased = false,
  selectedId,
  reduceMotion,
  isDisabled,
  onBuy,
  renderOffer,
  purchaseFeedback,
  className = '',
}) => {
  const statusRef = useRef(null);
  const gridRef = useRef(null);
  const [reservedHeight, setReservedHeight] = useState(null);
  const [exitComplete, setExitComplete] = useState(purchased);

  const buyOffer = (id) => {
    const height = gridRef.current?.getBoundingClientRect().height;
    if (height) setReservedHeight(height);
    setExitComplete(false);
    onBuy(id);
  };

  useEffect(() => {
    if (purchased && selectedId && exitComplete)
      statusRef.current?.focus({ preventScroll: true });
  }, [purchased, selectedId, exitComplete]);

  return (
    <div
      ref={gridRef}
      className={`coreboundCardGrid coreboundStoreGrid ${className} ${
        purchased ? 'is-spent' : ''
      } ${purchased && exitComplete ? 'is-confirmed' : ''}`}
      style={
        purchased && reservedHeight
          ? { minHeight: `${reservedHeight}px` }
          : undefined
      }
      inert={purchased && !exitComplete ? '' : undefined}
      aria-hidden={purchased && !exitComplete ? true : undefined}
    >
      <AnimatePresence
        initial={false}
        custom={selectedId}
        onExitComplete={() => setExitComplete(true)}
      >
        {(purchased ? [] : offers).map((id, index) => (
          <motion.button
            key={id}
            type="button"
            layout={!reduceMotion}
            initial={false}
            animate="available"
            exit="dismissed"
            variants={{
              available: {
                opacity: isDisabled(id) ? 0.5 : 1,
                x: 0,
                y: 0,
                scale: 1,
                skewX: 0,
              },
              dismissed: getOfferExit(id, index, reduceMotion),
            }}
            className={`coreboundChoice coreboundRelicChoice coreboundStoreRelic coreboundRarityCard-${(
              RELICS[id].category || RELICS[id].rarity
            ).toLowerCase()}`}
            disabled={purchased || isDisabled(id)}
            onClick={() => buyOffer(id)}
          >
            {renderOffer(id)}
          </motion.button>
        ))}
      </AnimatePresence>
      {purchased && exitComplete && (
        <div className="coreboundStoreReceipt">
          <motion.div
            ref={statusRef}
            className={
              purchaseFeedback
                ? 'coreboundStoreReceiptFeedback'
                : 'coreboundStorePurchaseStatus'
            }
            role="status"
            tabIndex={-1}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: reduceMotion ? 0 : 0.22,
              ease: 'easeOut',
            }}
          >
            {purchaseFeedback || (
              <>
                <strong>RELIC ADDED</strong>
                <span>
                  One item per visit. You can still heal before leaving.
                </span>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RelicStoreOffers;
